/**
 * Owns the screen-share lifecycle from the call screen's perspective.
 *
 * - `requestFromAi(reason)` - triggered when the worker forwards a
 *   `screen_share_requested` message. Puts up a consent prompt.
 * - `accept()` / `decline()` - resolve the AI-initiated prompt.
 * - `startManual()` - user clicked the "Share screen" button in the controls.
 * - `onEndRequestedFromAi(reason)` - worker told us to stop (AI called
 *   `end_screen_share` or the duration cap fired).
 * - `stop(reason)` - local stop (manual button, component unmount, or fallthrough
 *   from the browser's "Stop sharing" UI surfaced by the service).
 *
 * Returned methods are stabilized via useCallback so useSessionWebSocket can
 * destructure them into stable dependency arrays (same pattern as
 * useAudioPipeline - see the BUG FIX note in useSessionWebSocket.ts).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { captureEvent, captureException } from '../../../lib/telemetry/posthog/frontend';
import { buildLiveInterviewBaseProps } from '../../../lib/telemetry/live-interview-contract';
import {
  createScreenShareService,
  type ScreenShareService,
  type ScreenShareStopReason,
} from '../../../services/screenShareService';

interface UseScreenShareOptions {
  wsRef: React.MutableRefObject<WebSocket | null>;
  wsReadyRef: React.MutableRefObject<boolean>;
  /** Canonical analytics id: room id (same as `flow_id` in PostHog). */
  flowId: string;
  interviewDataId?: string;
  /** Whether the campaign has the feature enabled (governs UI availability). */
  screenShareEnabled: boolean;
  /**
   * Returns the live microphone MediaStream from the audio pipeline (or null).
   * Passed through to `ScreenShareService.start` so the screen recording can
   * clone the mic audio into its WebM - otherwise the recording is silent.
   */
  getAudioStream?: () => MediaStream | null;
}

export interface ScreenShareRequest {
  /** User-facing reason the AI wants to see the screen. */
  reason: string;
}

export function useScreenShare({ wsRef, wsReadyRef, flowId, interviewDataId, screenShareEnabled, getAudioStream }: UseScreenShareOptions) {
  const [isSharing, setIsSharing] = useState(false);
  const [pendingRequest, setPendingRequest] = useState<ScreenShareRequest | null>(null);
  const [lastError, setLastError] = useState<string | null>(null);

  const serviceRef = useRef<ScreenShareService | null>(null);

  /** Send a JSON message to the live-session worker. Mirrors useAudioPipeline.sendToWorker. */
  const sendToWorker = useCallback(
    (data: unknown) => {
      const ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify(data));
      }
    },
    [wsRef],
  );

  /** Shared stop path - tears down the service and notifies the worker once. */
  const stop = useCallback(
    (reason: ScreenShareStopReason) => {
      const service = serviceRef.current;
      if (service && service.isActive()) {
        // `stop()` triggers our onStopped callback which does the WS notify +
        // state reset. Don't duplicate those here.
        service.stop(reason);
        return;
      }
      // No active service - just flip UI state (defensive).
      if (isSharing) {
        sendToWorker({ type: 'screen_share_stopped', reason });
        setIsSharing(false);
      }
    },
    [isSharing, sendToWorker],
  );

  /**
   * Common start path used by both `accept()` and `startManual()`. Acquires
   * the display stream, wires up onStopped/onError, and notifies the worker
   * once the stream is live.
   */
  const beginShare = useCallback(
    async (source: 'ai_initiated' | 'user_initiated') => {
      if (!screenShareEnabled) return;
      if (serviceRef.current?.isActive()) return;
      if (!wsRef.current || !wsReadyRef.current) {
        setLastError('Not connected yet');
        return;
      }

      const service = createScreenShareService();
      serviceRef.current = service;
      setLastError(null);
      if (source === 'user_initiated') {
        captureEvent('live_interview.screen_share_prompt_shown', {
          ...buildLiveInterviewBaseProps(flowId, 'in_call', interviewDataId),
          source: 'user',
        });
      }

      // Tell the worker the browser picker is about to open. The worker relays
      // this upstream as an authoritative [SYSTEM] counter-signal so she
      // doesn't hallucinate seeing the screen based on the user's verbal
      // "I'm sharing now" while the picker is still open.
      sendToWorker({ type: 'screen_share_picker_opened', source });

      try {
        await service.start({
          websocket: wsRef.current,
          getAudioStream,
          onStarted: () => {
            setIsSharing(true);
            sendToWorker({ type: 'screen_share_started', source });
            captureEvent('live_interview.screen_share_started', {
              ...buildLiveInterviewBaseProps(flowId, 'in_call', interviewDataId),
              source: source === 'ai_initiated' ? 'ai' : 'user',
            });
          },
          onStopped: (reason) => {
            setIsSharing(false);
            serviceRef.current = null;
            sendToWorker({ type: 'screen_share_stopped', reason });
            const normalizedReason = reason === 'browser_stopped' || reason === 'error'
              ? 'browser_ended'
              : reason;
            const eventSource = reason === 'ai_ended'
              ? 'ai'
              : (reason === 'user_stopped' ? 'user' : 'system');
            captureEvent('live_interview.screen_share_stopped', {
              ...buildLiveInterviewBaseProps(flowId, 'in_call', interviewDataId),
              reason: normalizedReason,
              source: eventSource,
            });
          },
          onError: (err) => {
            setLastError(err.message);
            sendToWorker({ type: 'screen_share_error', message: err.message });
          },
        });
      } catch (err) {
        serviceRef.current = null;
        const message = err instanceof Error ? err.message : String(err);
        setLastError(message);
        captureException(err instanceof Error ? err : message, 'useScreenShare.beginShare', { source });

        // NotAllowedError means the user cancelled the browser prompt - treat
        // that as a decline rather than an error so the AI gets a clean signal.
        const isPermissionDenied = err instanceof DOMException && err.name === 'NotAllowedError';
        if (isPermissionDenied) {
          sendToWorker({ type: 'screen_share_declined' });
          captureEvent('live_interview.screen_share_declined', {
            ...buildLiveInterviewBaseProps(flowId, 'in_call', interviewDataId),
            source: source === 'ai_initiated' ? 'ai' : 'user',
            reason: 'permission_denied',
          });
        } else {
          sendToWorker({ type: 'screen_share_error', message });
        }
      }
    },
    [flowId, interviewDataId, screenShareEnabled, wsRef, wsReadyRef, sendToWorker, getAudioStream],
  );

  /** Wired to the `screen_share_requested` server message. */
  const requestFromAi = useCallback(
    (reason: string) => {
      if (!screenShareEnabled) return;
      setPendingRequest({ reason });
      captureEvent('live_interview.screen_share_prompt_shown', {
        ...buildLiveInterviewBaseProps(flowId, 'in_call', interviewDataId),
        source: 'ai',
      });
    },
    [flowId, interviewDataId, screenShareEnabled],
  );

  /** User accepted the AI-initiated prompt. */
  const accept = useCallback(async () => {
    setPendingRequest(null);
    await beginShare('ai_initiated');
  }, [beginShare]);

  /** User declined the AI-initiated prompt. */
  const decline = useCallback(() => {
    setPendingRequest(null);
    sendToWorker({ type: 'screen_share_declined' });
    captureEvent('live_interview.screen_share_declined', {
      ...buildLiveInterviewBaseProps(flowId, 'in_call', interviewDataId),
      source: 'ai',
      reason: 'user_declined',
    });
  }, [flowId, interviewDataId, sendToWorker]);

  /** User clicked the "Share screen" button in the bottom controls. */
  const startManual = useCallback(async () => {
    await beginShare('user_initiated');
  }, [beginShare]);

  /** Wired to the `screen_share_end_requested` server message. */
  const onEndRequestedFromAi = useCallback(
    (_reason: string) => {
      stop('ai_ended');
    },
    [stop],
  );

  /** Called by `useSessionWebSocket.cleanupResources` on session teardown. */
  const cleanup = useCallback(() => {
    setPendingRequest(null);
    const service = serviceRef.current;
    if (service?.isActive()) {
      // Session is ending. Tell the model just once so it does not narrate into the void.
      service.stop('session_ending');
    }
    serviceRef.current = null;
    setIsSharing(false);
  }, []);

  // Ensure we don't leak a service if the component unmounts unexpectedly.
  useEffect(() => {
    return () => {
      const service = serviceRef.current;
      if (service?.isActive()) service.stop('session_ending');
      serviceRef.current = null;
    };
  }, []);

  return {
    // State
    isSharing,
    pendingRequest,
    lastError,
    canShare: screenShareEnabled,
    // Commands
    requestFromAi,
    onEndRequestedFromAi,
    accept,
    decline,
    startManual,
    stop,
    cleanup,
  };
}
